import { useState, useEffect } from 'react'
import {
  Phone,
  Video,
  Mic,
  Camera,
  AlertCircle,
  CheckCircle,
  Settings,
  Loader2
} from 'lucide-react'
import { MediaService } from '../../services/MediaService'
import { toastManager, TOAST_GROUPS } from '../../utils/toastManager'
import { useP2PStore } from '../../store/p2pStore'

interface DesktopCallButtonProps {
  targetPeerId: string
  targetAlias: string
  mediaService: MediaService | null
  onCallInitiated?: (callType: 'voice' | 'video') => void
  isConnected: boolean
  className?: string
}

interface PermissionStatus {
  audio: boolean
  video: boolean
  checking: boolean
  error?: string
}

export function DesktopCallButton({
  targetPeerId,
  targetAlias,
  mediaService,
  onCallInitiated,
  isConnected,
  className = ''
}: DesktopCallButtonProps) {
  const [permissions, setPermissions] = useState<PermissionStatus>({
    audio: false,
    video: false,
    checking: false
  })
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false)
  const [isInitiatingCall, setIsInitiatingCall] = useState(false)

  // Get connection manager from store
  const connectionManager = useP2PStore(state => state.connectionManager)

  // Detect if this is a desktop user
  const isDesktop = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))

  // Check permissions on component mount and periodically
  useEffect(() => {
    if (!isDesktop || !mediaService) return

    const checkPermissions = async () => {
      try {
        const capabilities = await mediaService.checkMediaCapabilities()
        setPermissions(prev => ({
          ...prev,
          audio: capabilities.permissions.microphone === 'granted',
          video: capabilities.permissions.camera === 'granted',
          checking: false
        }))
      } catch (error) {
        console.warn('Failed to check permissions:', error)
        setPermissions(prev => ({ ...prev, checking: false }))
      }
    }

    checkPermissions()

    // Check permissions every 30 seconds for desktop users
    const interval = setInterval(checkPermissions, 30000)
    return () => clearInterval(interval)
  }, [isDesktop, mediaService])

  const requestPermissions = async () => {
    if (!mediaService || !isDesktop) return

    setPermissions(prev => ({ ...prev, checking: true }))

    try {
      console.log('🖥️ Requesting desktop permissions step-by-step...')
      const results = await mediaService.requestPermissionsStepByStep()

      const newPermissions: PermissionStatus = {
        audio: results.audioGranted,
        video: results.videoGranted,
        checking: false
      }
      if (results.error) {
        newPermissions.error = results.error
      }
      setPermissions(newPermissions)

      if (results.audioGranted && results.videoGranted) {
        toastManager.success('Camera and microphone ready for calls!', TOAST_GROUPS.CALL, {
          duration: 3000,
          icon: '✅'
        })
        setShowPermissionPrompt(false)
      } else if (results.audioGranted) {
        toastManager.success('Microphone ready for voice calls!', TOAST_GROUPS.CALL, {
          duration: 3000,
          icon: '🎤'
        })
        if (!results.error) {
          toastManager.info('Grant camera access for video calls', TOAST_GROUPS.CALL, {
            duration: 4000,
            icon: '📹'
          })
        }
        setShowPermissionPrompt(false)
      } else {
        // Show specific error guidance
        const errorMessage = results.error || 'Failed to get media permissions'
        toastManager.error(errorMessage, TOAST_GROUPS.CALL, {
          duration: 8000,
          icon: '❌'
        })
      }
    } catch (error) {
      console.error('Permission request failed:', error)
      setPermissions(prev => ({
        ...prev,
        checking: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }))
      toastManager.error('Permission request failed', TOAST_GROUPS.CALL)
    }
  }

  const retryPermissions = async () => {
    // SURGICAL FIX: Clear permission states and cached streams for retry
    if (mediaService) {
      // Reset permission states to allow fresh attempts
      mediaService.resetPermissionStates()
      mediaService.refreshMediaStreams()

      // Brief delay to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 500))

      // Clear any previous errors
      setPermissions(prev => {
        const newState = { ...prev }
        delete newState.error
        return newState
      })

      // Attempt permissions again
      await requestPermissions()
    }
  }

  const initiateCall = async (callType: 'voice' | 'video') => {
    if (!connectionManager || !isConnected) return

    setIsInitiatingCall(true)

    try {
      console.log(`🖥️ Desktop initiating ${callType} call to ${targetAlias} (${targetPeerId})`)

      let success = false

      if (callType === 'voice') {
        success = await connectionManager.initiateVoiceCall(targetPeerId)
      } else {
        success = await connectionManager.initiateVideoCall(targetPeerId)
      }

      if (success) {
        onCallInitiated?.(callType)
        toastManager.success(`${callType === 'voice' ? 'Voice' : 'Video'} call initiated`, TOAST_GROUPS.CALL, {
          icon: callType === 'voice' ? '📞' : '📹'
        })
      } else {
        throw new Error('Call initiation failed')
      }
    } catch (error) {
      console.error(`${callType} call failed:`, error)
      toastManager.error(`${callType === 'voice' ? 'Voice' : 'Video'} call failed`, TOAST_GROUPS.CALL)
    } finally {
      setIsInitiatingCall(false)
    }
  }

  const getPermissionIcon = () => {
    if (permissions.checking) return <Loader2 className="w-4 h-4 animate-spin" />
    if (permissions.audio && permissions.video) return <CheckCircle className="w-4 h-4 text-success-500" />
    if (permissions.audio) return <Mic className="w-4 h-4 text-warning-500" />
    return <AlertCircle className="w-4 h-4 text-error-500" />
  }

  const getPermissionStatus = () => {
    if (permissions.checking) return 'Checking permissions...'
    if (permissions.audio && permissions.video) return 'Ready for voice & video calls'
    if (permissions.audio) return 'Ready for voice calls only'
    return 'Permissions needed for calls'
  }

  // Don't render for mobile users
  if (!isDesktop) {
    return null
  }

  return (
    <div className={`relative ${className}`}>
      {/* Main Call Buttons */}
      <div className="flex items-center space-x-2">
        {/* Voice Call Button */}
        <button
          onClick={() => {
            if (!permissions.audio) {
              setShowPermissionPrompt(true)
              return
            }
            initiateCall('voice')
          }}
          disabled={!isConnected || isInitiatingCall}
          className={`p-3 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
            permissions.audio
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
          }`}
          title={
            !isConnected
              ? 'Not connected to peer'
              : !permissions.audio
              ? 'Click to request microphone permission'
              : `Voice call ${targetAlias}`
          }
        >
          {isInitiatingCall ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Phone className="w-5 h-5" />
          )}
        </button>

        {/* Video Call Button */}
        <button
          onClick={() => {
            if (!permissions.video || !permissions.audio) {
              setShowPermissionPrompt(true)
              return
            }
            initiateCall('video')
          }}
          disabled={!isConnected || isInitiatingCall}
          className={`p-3 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
            permissions.video && permissions.audio
              ? 'bg-blue-500 hover:bg-blue-600 text-white'
              : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
          }`}
          title={
            !isConnected
              ? 'Not connected to peer'
              : !permissions.video || !permissions.audio
              ? 'Click to request camera & microphone permissions'
              : `Video call ${targetAlias}`
          }
        >
          {isInitiatingCall ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Video className="w-5 h-5" />
          )}
        </button>

        {/* Permission Status Indicator */}
        <div
          className="flex items-center space-x-1 px-2 py-1 bg-gray-100 rounded-lg cursor-pointer"
          onClick={() => setShowPermissionPrompt(true)}
          title={getPermissionStatus()}
        >
          {getPermissionIcon()}
          <span className="text-xs text-gray-600 hidden sm:inline">
            {permissions.audio && permissions.video ? 'Ready' : 'Setup'}
          </span>
        </div>
      </div>

      {/* Permission Setup Modal */}
      {showPermissionPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Setup Desktop Calling</h3>
              <button
                onClick={() => setShowPermissionPrompt(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                To make high-quality calls with {targetAlias}, we need access to your camera and microphone.
              </div>

              {/* Permission Status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Mic className="w-4 h-4" />
                    <span className="text-sm">Microphone</span>
                  </div>
                  {permissions.audio ? (
                    <CheckCircle className="w-4 h-4 text-success-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-error-500" />
                  )}
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Camera className="w-4 h-4" />
                    <span className="text-sm">Camera</span>
                  </div>
                  {permissions.video ? (
                    <CheckCircle className="w-4 h-4 text-success-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-error-500" />
                  )}
                </div>
              </div>

              {permissions.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-700 font-semibold">Permission Error</span>
                  </div>
                  <p className="text-sm text-red-700 mb-2">{permissions.error}</p>
                  <div className="text-xs text-red-600">
                    <strong>How to fix:</strong>
                    <br />1. Look for camera/microphone icons in your browser's address bar
                    <br />2. Click on them and select "Allow"
                    <br />3. Click "Try Again" below (no page refresh needed)
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                {!permissions.audio && !permissions.video ? (
                  <button
                    onClick={requestPermissions}
                    disabled={permissions.checking}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {permissions.checking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Settings className="w-4 h-4" />
                    )}
                    <span>{permissions.checking ? 'Requesting...' : 'Grant Permissions'}</span>
                  </button>
                ) : permissions.error ? (
                  <button
                    onClick={retryPermissions}
                    disabled={permissions.checking}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {permissions.checking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Settings className="w-4 h-4" />
                    )}
                    <span>{permissions.checking ? 'Retrying...' : 'Try Again'}</span>
                  </button>
                ) : (
                  <button
                    onClick={requestPermissions}
                    disabled={permissions.checking}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {permissions.checking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Video className="w-4 h-4" />
                    )}
                    <span>{permissions.checking ? 'Requesting...' : 'Grant Camera Access'}</span>
                  </button>
                )}

                <button
                  onClick={() => setShowPermissionPrompt(false)}
                  className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>

              <div className="text-xs text-gray-500 space-y-1">
                <div>💡 <strong>Browser Permissions:</strong></div>
                <div>• <strong>Chrome/Edge:</strong> Click camera/mic icon in address bar</div>
                <div>• <strong>Firefox:</strong> Click shield icon for permissions</div>
                <div>• <strong>Safari:</strong> Check Settings → Websites → Camera/Microphone</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}